import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import {
  dispatchToDelman,
  saveDispatch,
  type DispatchLineItem,
  type DispatchListItem,
} from '@/features/warehouse/api/dispatchApi'
import { fetchMaterialStock, type MaterialStockItem } from '@/features/warehouse/api/materialStock'
import { fetchDelmans, fetchKitchens, type DelmanPickerItem, type KitchenPickerItem } from '@/features/warehouse/api/pickers'
import {
  aggregateFilledQtyByUom,
  formatEditorCategorySummary,
  groupEditorLinesByCategory,
  shouldEditorCategoryDefaultOpen,
  type DispatchEditorLine,
} from '@/features/warehouse/dispatchEditorGroups'
import { formatQtyByUom } from '@/features/warehouse/stockGroups'
import { warehouseTerms } from '@/features/warehouse/warehouseTerms'
import { canPerformHeadActions } from '@/shared/auth/canPerformHeadActions'
import { getWarehouseSession } from '@/shared/auth/warehouseSessionStorage'
import { CollapsibleCategoryBlock } from '@/shared/components/CollapsibleCategoryBlock'
import { PinKeypadModal } from '@/shared/components/PinKeypadModal'
import { QtyStepper } from '@/shared/components/QtyStepper'
import { SkeletonList } from '@/shared/components/Skeleton'
import { StickyActionFooter } from '@/shared/components/StickyActionFooter'
import { Toast } from '@/shared/components/Toast'

function buildEditorLines(stock: MaterialStockItem[], existing: DispatchLineItem[]): DispatchEditorLine[] {
  const existingByMaterial = new Map(existing.map((l) => [l.material_id, l]))
  const lines: DispatchEditorLine[] = stock.map((item) => {
    const ex = existingByMaterial.get(item.material_id)
    return {
      materialId: item.material_id,
      materialCode: item.material_code,
      materialName: item.material_name,
      uom: ex?.uom ?? item.uom,
      qty: ex?.qty ?? 0,
      // Re-dispatch mirror backend: movement lama direverse dulu sebelum cek stok,
      // jadi qty baris yang sudah tersimpan tetap "tersedia" untuk baris itu sendiri.
      maxQty: item.quantity_on_hand + (ex?.qty ?? 0),
      categoryId: item.category_id,
      categoryName: item.category_name,
    }
  })
  for (const ex of existing) {
    if (!stock.some((s) => s.material_id === ex.material_id)) {
      lines.push({
        materialId: ex.material_id,
        materialCode: ex.material_code,
        materialName: ex.material_name,
        uom: ex.uom,
        qty: ex.qty,
        maxQty: ex.qty,
      })
    }
  }
  return lines
}

/**
 * Buat/edit draft pengiriman + serah PIN ke delman (§5 spec). ref = "new" (baru)
 * atau id/code dispatch existing. Saat edit existing, data diambil dari
 * location.state (dilempar dari DispatchListPage) — belum ada GET single dispatch.
 */
export function WarehouseDispatchEditorPage() {
  const params = useParams<{ ref: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const existingDispatch = (location.state as { dispatch?: DispatchListItem } | null)?.dispatch

  const isNew = !existingDispatch
  const status = existingDispatch?.status
  const isEditable = isNew || status === 'draft' || status === 'dispatched'
  const canSaveDraft = isNew || status === 'draft'
  const canDispatch = isNew || status === 'draft' || status === 'dispatched'
  const session = getWarehouseSession()
  const canPerformDispatch = canPerformHeadActions(session ?? {})
  const headPresentMessage = 'Kepala sedang di tempat — aksi ini hanya oleh kepala atau setelah kepala checkout.'

  const [currentRef, setCurrentRef] = useState(existingDispatch?.id ?? params.ref ?? 'new')
  const [kitchens, setKitchens] = useState<KitchenPickerItem[]>([])
  const [delmans, setDelmans] = useState<DelmanPickerItem[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)

  const [targetKitchenId, setTargetKitchenId] = useState('')
  const [delmanId, setDelmanId] = useState('')
  const [lines, setLines] = useState<DispatchEditorLine[]>([])
  const [showAllLines, setShowAllLines] = useState(false)
  const [refreshKey, setRefreshKey] = useState(0)

  const [pinOpen, setPinOpen] = useState(false)
  const [toast, setToast] = useState<string | null>(null)
  const [formError, setFormError] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    let cancelled = false
    setIsLoading(true)
    setLoadError(false)
    Promise.all([fetchKitchens(), fetchDelmans(), fetchMaterialStock()])
      .then(([kitchenList, delmanList, stockList]) => {
        if (cancelled) return
        setKitchens(kitchenList)
        setDelmans(delmanList)

        const matchedKitchen = existingDispatch
          ? kitchenList.find((k) => k.name === existingDispatch.target_kitchen_name)
          : undefined
        setTargetKitchenId(matchedKitchen?.id ?? kitchenList[0]?.id ?? '')

        const matchedDelman = existingDispatch
          ? delmanList.find((d) => d.name === existingDispatch.warehouse_delman_name)
          : undefined
        setDelmanId(matchedDelman?.id ?? delmanList[0]?.id ?? '')

        setLines(buildEditorLines(stockList, existingDispatch?.lines ?? []))
      })
      .catch(() => {
        if (!cancelled) setLoadError(true)
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey])

  const groups = useMemo(() => groupEditorLinesByCategory(lines, showAllLines), [lines, showAllLines])
  const payloadLines = useMemo(() => lines.filter((l) => l.qty > 0), [lines])
  const footerQtySummary = useMemo(
    () => formatQtyByUom(aggregateFilledQtyByUom(payloadLines)),
    [payloadLines],
  )

  function setLineQty(materialId: string, qty: number) {
    setLines((prev) => prev.map((l) => (l.materialId === materialId ? { ...l, qty } : l)))
  }

  function validate(): boolean {
    if (!targetKitchenId) {
      setFormError(warehouseTerms.dispatchEditor.errors.targetKitchenRequired)
      return false
    }
    if (!delmanId) {
      setFormError(warehouseTerms.dispatchEditor.errors.delmanRequired)
      return false
    }
    if (payloadLines.length === 0) {
      setFormError(warehouseTerms.dispatchEditor.errors.minOneLine)
      return false
    }
    setFormError(null)
    return true
  }

  async function handleSaveDraft() {
    if (!validate()) return
    setIsSaving(true)
    try {
      const res = await saveDispatch(currentRef, {
        targetKitchenId,
        warehouseDelmanUserId: delmanId,
        lines: payloadLines.map((l) => ({ materialId: l.materialId, uom: l.uom, qty: l.qty })),
      })
      setCurrentRef(res.dispatch_id)
      setToast(warehouseTerms.dispatchEditor.toast.draftSaved)
      navigate('/warehouse/dispatches')
    } catch (e) {
      setFormError(e instanceof Error ? e.message : warehouseTerms.dispatchEditor.toast.saveFailed)
    } finally {
      setIsSaving(false)
    }
  }

  function openPin() {
    if (!validate()) return
    if (!canPerformDispatch) {
      setFormError(
        session?.roleCode === 'ASSISTANT_HEAD_WAREHOUSE' && session.headPresent
          ? headPresentMessage
          : 'Aksi serah ke delman hanya dapat dilakukan oleh kepala gudang.',
      )
      return
    }
    setPinOpen(true)
  }

  async function handleDispatch(pin: string) {
    try {
      const res = await dispatchToDelman(
        currentRef,
        {
          targetKitchenId,
          warehouseDelmanUserId: delmanId,
          lines: payloadLines.map((l) => ({ materialId: l.materialId, uom: l.uom, qty: l.qty })),
        },
        pin,
      )
      setCurrentRef(res.dispatch_id)
      setToast(warehouseTerms.dispatchEditor.toast.dispatched)
      setPinOpen(false)
      navigate('/warehouse/dispatches')
    } catch (error) {
      const message = error instanceof Error ? error.message : ''
      if (message.toLowerCase().includes('head_present') || message.includes('Kepala sedang di tempat')) {
        throw new Error(headPresentMessage)
      }
      throw error
    }
  }

  const title = existingDispatch
    ? warehouseTerms.dispatchEditor.titleExisting(existingDispatch.code)
    : warehouseTerms.dispatchEditor.titleNew

  return (
    <section className="space-y-4 pb-28">
      <div className="card space-y-3">
        <p className="page-title break-words">{title}</p>

        {isLoading && <SkeletonList count={2} label={warehouseTerms.dispatchEditor.loading} />}

        {loadError && (
          <div role="alert" className="alert-error flex flex-wrap items-center justify-between gap-2">
            <span className="text-body-sm">{warehouseTerms.dispatchEditor.loadError}</span>
            <button type="button" className="btn-outline min-h-[36px] text-body-sm" onClick={() => setRefreshKey((k) => k + 1)}>
              {warehouseTerms.dispatchEditor.retry}
            </button>
          </div>
        )}

        {!isLoading && !loadError && (
          <>
            {!isEditable && (
              <p className="text-body-sm text-status-warning bg-status-warning-bg rounded-lg px-3 py-2">
                {warehouseTerms.dispatchEditor.readOnlyHint}
              </p>
            )}

            <div className="space-y-2">
              <label className="field-label" htmlFor="wd_target_kitchen">
                {warehouseTerms.dispatchEditor.targetKitchen}
              </label>
              <select
                id="wd_target_kitchen"
                className="input-field"
                value={targetKitchenId}
                disabled={!isEditable}
                onChange={(e) => setTargetKitchenId(e.target.value)}
              >
                {kitchens.length === 0 && <option value="">{warehouseTerms.dispatchEditor.targetKitchenPlaceholder}</option>}
                {kitchens.map((k) => (
                  <option key={k.id} value={k.id}>
                    {k.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <label className="field-label" htmlFor="wd_delman">
                {warehouseTerms.dispatchEditor.delman}
              </label>
              <select
                id="wd_delman"
                className="input-field"
                value={delmanId}
                disabled={!isEditable}
                onChange={(e) => setDelmanId(e.target.value)}
              >
                {delmans.length === 0 && <option value="">{warehouseTerms.dispatchEditor.delmanPlaceholder}</option>}
                {delmans.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </div>
          </>
        )}
      </div>

      {!isLoading && !loadError && (
        <div className="card space-y-3">
          <div className="flex items-center justify-between gap-2">
            <div>
              <p className="text-body-sm font-semibold text-on-surface">{warehouseTerms.dispatchEditor.materialSectionTitle}</p>
              <p className="text-body-sm text-on-surface-variant mt-0.5">{warehouseTerms.dispatchEditor.materialSectionHint}</p>
            </div>
            {isEditable && (
              <button
                type="button"
                className="text-body-sm text-primary font-semibold shrink-0"
                onClick={() => setShowAllLines((prev) => !prev)}
              >
                {showAllLines ? warehouseTerms.dispatchEditor.hideEmpty : warehouseTerms.dispatchEditor.showAll}
              </button>
            )}
          </div>

          <div className="space-y-2">
            {groups.map((g, i) => (
              <CollapsibleCategoryBlock
                key={g.categoryKey}
                categoryName={g.categoryName}
                itemCount={g.itemCount}
                itemCountLabel={warehouseTerms.stock.itemCountLabel}
                summary={formatEditorCategorySummary(g) || undefined}
                defaultOpen={shouldEditorCategoryDefaultOpen(g, i, groups.length)}
              >
                {g.items.map((line) => (
                  <div
                    key={line.materialId}
                    className="flex items-center justify-between gap-3 px-3 py-2.5 border-b border-outline-variant last:border-b-0"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-body-sm font-semibold text-on-surface break-words">{line.materialName}</p>
                      <p className="text-body-sm text-on-surface-variant">
                        {line.maxQty > 0
                          ? warehouseTerms.dispatchEditor.maxStock(line.maxQty, line.uom)
                          : warehouseTerms.dispatchEditor.noStock}
                      </p>
                    </div>
                    <QtyStepper
                      value={line.qty}
                      min={0}
                      max={line.maxQty}
                      disabled={!isEditable || line.maxQty <= 0}
                      onChange={(qty) => setLineQty(line.materialId, qty)}
                    />
                  </div>
                ))}
              </CollapsibleCategoryBlock>
            ))}
          </div>

          <div className="rounded-lg bg-surface-container p-3">
            <p className="text-body-sm font-semibold text-on-surface">
              {warehouseTerms.dispatchEditor.footerFilled(payloadLines.length, footerQtySummary)}
            </p>
          </div>
        </div>
      )}

      {formError && (
        <div role="alert" className="alert-error">
          <span className="material-symbols-outlined text-base shrink-0">error</span>
          <span>{formError}</span>
        </div>
      )}

      {!isLoading && !loadError && (
        <StickyActionFooter>
          <div className="flex gap-2">
            <button
              type="button"
              className="btn-outline flex-1 min-h-[44px]"
              onClick={() => navigate('/warehouse/dispatches')}
            >
              {warehouseTerms.dispatchEditor.back}
            </button>
            {canSaveDraft && (
              <button
                type="button"
                className="btn-outline flex-1 min-h-[44px]"
                disabled={isSaving}
                onClick={() => void handleSaveDraft()}
              >
                {warehouseTerms.dispatchEditor.saveDraft}
              </button>
            )}
            {canDispatch && (
              <button type="button" className="btn-primary flex-1 min-h-[44px] text-center" onClick={openPin}>
                {status === 'dispatched' ? warehouseTerms.dispatchEditor.reDispatch : warehouseTerms.dispatchEditor.dispatch}
              </button>
            )}
          </div>
        </StickyActionFooter>
      )}

      <PinKeypadModal
        open={pinOpen}
        title={warehouseTerms.dispatchEditor.pin.title}
        description={warehouseTerms.dispatchEditor.pin.description}
        confirmLabel={warehouseTerms.dispatchEditor.pin.confirm}
        onClose={() => setPinOpen(false)}
        onConfirm={handleDispatch}
      />

      {toast && <Toast message={toast} onDismiss={() => setToast(null)} />}
    </section>
  )
}
